import React from 'react';
import { RefreshCw, Trash2, Send } from 'lucide-react';
import type { ColumnMenuState } from '../../../types/table';
import { getAppUrl } from '../../../utils/validation';
import { TABLE_Z_INDEX } from '../../../constants/table';

export interface ColumnEnrichmentMenuProps {
  menuState: ColumnMenuState;
  isPublicView?: boolean;
  isEnriching?: boolean;
  tableType?: string;
  onRerunEnrichments: (columnName: string) => void;
  onBulkEmailClick: (columnName: string) => void;
  onDeleteClick: () => void;
  onClose: () => void;
  logClick?: (element: HTMLElement, data: Record<string, any>) => void;
}

/**
 * Column enrichment popup menu with options for:
 * - Rerun Incomplete Enrichments
 * - Send Email Campaign (for email columns)
 * - Delete Column
 */
export const ColumnEnrichmentMenu: React.FC<ColumnEnrichmentMenuProps> = ({
  menuState,
  isPublicView = false,
  isEnriching = false,
  tableType,
  onRerunEnrichments,
  onBulkEmailClick,
  onDeleteClick,
  onClose,
  logClick,
}) => {
  if (!menuState.show) return null;

  const isEmailColumn = menuState.columnName.toLowerCase().includes('email');

  const handleRerunClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onRerunEnrichments(menuState.columnName);
    logClick?.(e.currentTarget, {
      action: 'rerun_incomplete_enrichments',
      column_name: menuState.columnName,
      column_key: menuState.columnKey,
      table_type: tableType,
    });
    onClose();
  };

  const handleBulkEmailClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onBulkEmailClick(menuState.columnName);
    onClose();
  };

  const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onDeleteClick();
    logClick?.(e.currentTarget, {
      action: 'open_delete_column_modal',
      column_name: menuState.columnName,
      column_key: menuState.columnKey,
      table_type: tableType,
    });
    onClose();
  };

  return (
    <div
      className="popup-container fixed bg-white border border-gray-200 rounded shadow-lg p-2 w-72"
      style={{
        left: menuState.x,
        top: menuState.y,
        zIndex: TABLE_Z_INDEX.CONTEXT_MENU,
      }}
    >
      <div className="flex flex-col space-y-0.5">
        {isPublicView ? (
          <div className="p-3 text-center">
            <div className="text-gray-900 text-sm mb-3">Login to enrich this column</div>
            <button
              id="column-enrichment-login-btn"
              onClick={() => window.open(getAppUrl(), '_blank')}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
            >
              Login to Skylarq
            </button>
          </div>
        ) : (
          <>
            <button
              id="rerun-incomplete-enrichments-btn"
              onClick={handleRerunClick}
              disabled={isEnriching}
              className="flex items-center text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Rerun Incomplete Enrichments
            </button>

            {isEmailColumn && (
              <button
                id="bulk-email-campaign-btn"
                onClick={handleBulkEmailClick}
                className="flex items-center text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded transition-all duration-200 whitespace-nowrap"
              >
                <Send className="w-4 h-4 mr-2" />
                Send Email Campaign
              </button>
            )}

            <button
              id="delete-column-btn"
              onClick={handleDeleteClick}
              className="flex items-center text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded transition-all duration-200"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Column
            </button>
          </>
        )}
      </div>
    </div>
  );
};

ColumnEnrichmentMenu.displayName = 'ColumnEnrichmentMenu';
